import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import LazyImage from './LazyImage';
import LogoImg from '../images/spider.png';
import { useAuth, useUI } from '../hooks/useRedux';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import AdminConsole, { ADMIN_EMAIL } from './AdminConsole';

const Header = ({ isVerified = false, showNavbar = false }) => {
  const { user, signIn, signOut, isLoading, error, isRetrying, retryCount } =
    useAuth();
  const { isMenuOpen, toggleMenu, closeMenu } = useUI();
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [commentCount, setCommentCount] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'meta', 'commentStats'), snap => {
      const total = snap.data()?.total;
      if (typeof total === 'number') setCommentCount(total);
    });
    return unsub;
  }, []);

  const handleLogout = () => {
    signOut();
  };

  const toggleErrorDetails = () => {
    setShowErrorDetails(!showErrorDetails);
  };

  const scrollToSection = sectionId => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    const headerEl = document.querySelector('header');
    const offset = headerEl ? headerEl.getBoundingClientRect().height : 70;
    const top = element.getBoundingClientRect().top + window.scrollY - offset - 12;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  // Don't render anything when navbar is hidden (during splash screen)
  if (!showNavbar) {
    return null;
  }

  return (
    <>
    <Wrapper>
      <MobileMenuButton onClick={toggleMenu} aria-label="Toggle menu">
        <HamburgerIcon $isOpen={isMenuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </HamburgerIcon>
      </MobileMenuButton>

      <Links $isOpen={isMenuOpen} $isVerified={isVerified}>
        <NavLink
          href="#abelicious"
          aria-label="Navigate to About section"
          onClick={e => {
            e.preventDefault();
            scrollToSection('abelicious');
            closeMenu();
          }}
        >
          Abelicious
        </NavLink>
        <NavLink
          href="#music"
          aria-label="Navigate to Music section"
          onClick={e => {
            e.preventDefault();
            scrollToSection('music');
            closeMenu();
          }}
        >
          Music
        </NavLink>
        <NavLink
          href="#home"
          aria-label="Navigate to Contact section"
          onClick={e => {
            e.preventDefault();
            scrollToSection('home');
            closeMenu();
          }}
        >
          Contact
        </NavLink>
        <NavLink
          href="#alignment"
          aria-label="Navigate to Abel Alignment Test"
          onClick={e => {
            e.preventDefault();
            scrollToSection('alignment');
            closeMenu();
          }}
        >
          Alignment
        </NavLink>
        <NavLink
          href="#video"
          aria-label="Navigate to Comments section"
          onClick={e => {
            e.preventDefault();
            scrollToSection('video');
            closeMenu();
          }}
        >
          Comments
          {commentCount !== null && commentCount > 0 && (
            <CommentBadge>{commentCount > 999 ? '999+' : commentCount}</CommentBadge>
          )}
        </NavLink>
      </Links>

      <Container>
        {isLoading ? (
          <LoadingText>Loading...</LoadingText>
        ) : (
          <>
            {!user && (
              <Login
                onClick={() => signIn()}
                aria-label="Login with Google"
                disabled={isRetrying}
              >
                {isRetrying ? `Retrying... (${retryCount})` : '🍄 LOGIN'}
              </Login>
            )}
            {error && (
              <ErrorContainer>
                <ErrorText
                  onClick={toggleErrorDetails}
                  title={
                    showErrorDetails
                      ? 'Click to hide details'
                      : 'Click to see browser-specific help'
                  }
                >
                  ⚠️ {error.browserSpecificMessage || error.message || error}
                  {showErrorDetails && (
                    <ErrorDetailsDropdown>
                      <ErrorDetailsContent>
                        <ErrorTitle>🔧 Browser-Specific Help</ErrorTitle>
                        <ErrorMessage>
                          {error.browserSpecificMessage || error.message}
                        </ErrorMessage>
                        {error.recommendations && (
                          <RecommendationsList>
                            <RecommendationTitle>
                              💡 Recommended Actions:
                            </RecommendationTitle>
                            {error.recommendations.map((rec, index) => (
                              <RecommendationItem key={index}>
                                {rec}
                              </RecommendationItem>
                            ))}
                          </RecommendationsList>
                        )}
                        <ErrorMeta>
                          <div>
                            <strong>Browser:</strong>{' '}
                            {error.browser || 'Unknown'}
                          </div>
                          <div>
                            <strong>Error Code:</strong> {error.code}
                          </div>
                          <div>
                            <strong>Time:</strong> {error.timestamp}
                          </div>
                        </ErrorMeta>
                      </ErrorDetailsContent>
                    </ErrorDetailsDropdown>
                  )}
                </ErrorText>
              </ErrorContainer>
            )}
            {isAdmin && (
              <AdminBtn
                onClick={() => setShowAdmin(true)}
                title="Admin Console"
                aria-label="Open admin console"
              >
                👑
              </AdminBtn>
            )}
            {user && (
              <UserRow>
                <UserAvatar
                  as={LazyImage}
                  src={user.photoURL || ''}
                  alt={user.displayName || 'User'}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <LogoutBtn onClick={handleLogout} aria-label="Logout">
                  LOGOUT
                </LogoutBtn>
              </UserRow>
            )}
          </>
        )}
        <Logo as={LazyImage} src={LogoImg} alt="Abel's Web Logo" />
      </Container>
    </Wrapper>

    {isAdmin && (
      <AdminConsole
        user={user}
        isOpen={showAdmin}
        onClose={() => setShowAdmin(false)}
      />
    )}
    </>
  );
};

export default Header;

const Wrapper = styled.header`
  width: 100%;
  max-width: 100vw;
  height: 70px;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  background-color: #4c0065;
  overflow: hidden;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  padding: 0 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: 60px;
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    height: 60px;
    padding: 0 10px;
  }
`;

const Links = styled.nav`
  display: flex;
  justify-content: space-around;
  align-items: center;
  color: #f2f2f2;
  text-align: center;
  text-decoration: none;
  text-shadow: 2px 2px 8px #000000;

  @media (max-width: 768px) {
    position: fixed;
    top: 60px;
    left: 0;
    width: 300px;
    height: auto;
    background-color: rgba(76, 0, 101, 0.95);
    backdrop-filter: blur(10px);
    flex-direction: column;
    justify-content: flex-start;
    padding: 20px;
    gap: 15px;
    transform: ${props =>
      props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease-in-out;
    z-index: 999;
    border-radius: 0 0 12px 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 480px) {
    top: 60px;
    width: 280px;
    height: auto;
    padding: 15px;
    gap: 12px;
  }
`;
const CommentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #8b0000;
  color: white;
  font-size: 0.55rem;
  font-family: Arial, sans-serif;
  font-weight: bold;
  border-radius: 10px;
  padding: 1px 5px;
  margin-left: 5px;
  min-width: 18px;
  vertical-align: middle;
  line-height: 1.4;
`;

const NavLink = styled(Link)`
  height: 70px;
  padding: 8px;
  text-decoration: none;
  color: white;
  font-family: 'Creepster';
  font-size: 45px;
  text-shadow: 2px 2px 8px #000000;
  transition: all 0.3s ease;

  &:hover {
    background-color: #6a0080;
    color: black;
    text-decoration: none;
    outline: none;
    text-shadow: 2px 2px 8px rgba(90, 0, 157, 1);
    box-shadow: 1px 4px 13px 1px rgba(0, 0, 0, 0.75);
  }

  @media (max-width: 768px) {
    font-size: 24px;
    height: auto;
    padding: 12px 20px;
    width: 100%;
    text-align: center;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.1);

    &:hover {
      background-color: rgba(106, 0, 128, 0.8);
      border-color: rgba(255, 255, 255, 0.4);
      transform: scale(1.02);
    }
  }

  @media (max-width: 480px) {
    font-size: 20px;
    padding: 10px 15px;
    width: 100%;
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const Login = styled.button`
  width: 90px;
  height: 40px;
  text-align: center;
  border: none;
  background-color: #8b0000;
  color: black;
  padding: 10px 20px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px 2px;
  cursor: pointer;
  border-radius: 25px;
  transition: background-color 0.3s ease;
  font-size: 12px;

  &:hover:not(:disabled) {
    background-color: #660000;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 35px;
    font-size: 11px;
    padding: 8px 16px;
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 32px;
    font-size: 10px;
    padding: 6px 12px;
  }
`;

const Logo = styled.img`
  width: 70px;
  height: 50px;
  margin: 15px;
  margin-left: 30px;
  transition: transform 0.3s ease;

  &:hover {
    cursor: pointer;
    transform: scale(1.2);
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 35px;
    margin: 10px;
    margin-left: 15px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 30px;
    margin: 5px;
    margin-left: 10px;
  }
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserAvatar = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  object-fit: cover;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
  }
`;

const LogoutBtn = styled.button`
  height: 34px;
  padding: 0 14px;
  background-color: #8b0000;
  color: black;
  border: none;
  border-radius: 25px;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: #660000;
  }

  @media (max-width: 480px) {
    height: 30px;
    padding: 0 10px;
    font-size: 10px;
  }
`;

const AdminBtn = styled.button`
  background: rgba(76, 0, 101, 0.5);
  border: 1px solid rgba(148, 0, 211, 0.6);
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 4px 10px;
  line-height: 1;
  transition: background 0.2s, box-shadow 0.2s;
  &:hover {
    background: rgba(76, 0, 101, 0.85);
    box-shadow: 0 0 8px rgba(148, 0, 211, 0.5);
  }
`;

const LoadingText = styled.span`
  color: white;
  font-size: 1rem;
  margin-right: 15px;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  z-index: 1001;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;

const HamburgerIcon = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  position: relative;

  span {
    display: block;
    height: 3px;
    width: 100%;
    background-color: white;
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  ${props =>
    props.$isOpen &&
    `
    span:nth-child(1) {
      transform: rotate(45deg) translate(6px, 6px);
    }
    
    span:nth-child(2) {
      opacity: 0;
    }
    
    span:nth-child(3) {
      transform: rotate(-45deg) translate(6px, -6px);
    }
  `}

  @media (max-width: 480px) {
    width: 20px;
    height: 16px;

    span {
      height: 2px;
    }
  }
`;

const ErrorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ErrorText = styled.span`
  color: #ff6b6b;
  font-size: 14px;
  margin: 0 5px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  animation: pulse 2s infinite;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 300px;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 107, 107, 0.2);
    transform: scale(1.02);
  }

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
    100% {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    font-size: 12px;
    max-width: 250px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    max-width: 200px;
  }
`;

const ErrorDetailsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 5px;
  background: rgba(0, 0, 0, 0.95);
  border: 2px solid #ff6b6b;
  border-radius: 8px;
  padding: 0;
  z-index: 1000;
  min-width: 350px;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(255, 107, 107, 0.3);

  @media (max-width: 768px) {
    min-width: 300px;
    max-width: 90vw;
  }

  @media (max-width: 480px) {
    min-width: 250px;
    max-width: 95vw;
  }
`;

const ErrorDetailsContent = styled.div`
  padding: 15px;
  color: white;
`;

const ErrorTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #ff6b6b;
  font-size: 16px;
  font-weight: bold;
`;

const ErrorMessage = styled.p`
  margin: 0 0 15px 0;
  color: #f8f8f8;
  font-size: 14px;
  line-height: 1.4;
`;

const RecommendationsList = styled.div`
  margin-bottom: 15px;
`;

const RecommendationTitle = styled.h5`
  margin: 0 0 8px 0;
  color: #4caf50;
  font-size: 14px;
  font-weight: bold;
`;

const RecommendationItem = styled.div`
  margin: 4px 0;
  padding: 4px 8px;
  background: rgba(76, 175, 80, 0.1);
  border-left: 3px solid #4caf50;
  font-size: 13px;
  line-height: 1.3;
  color: #e8f5e8;
`;

const ErrorMeta = styled.div`
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 107, 107, 0.3);
  font-size: 12px;
  color: #aaa;

  div {
    margin: 3px 0;
  }
`;
